// 🔵 ARQUIVO COMPLETO — PublicCoursePreview.jsx

import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/api";

const BACKEND_URL = "https://imagensdaface.com.br";

const getContentUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${BACKEND_URL}${clean}`;
};

/* ============================================================
   MODAL CTA
============================================================ */
const CTAmodal = ({ open, onClose, course }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black opacity-40" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 z-10">
        <h2 className="text-2xl font-bold mb-2">Continuar assistindo</h2>
        <p className="text-gray-700 mb-4">
          Para continuar assistindo esta aula você precisa estar cadastrado ou ter comprado o curso.
        </p>

        <div className="flex gap-3">
          <Link to="/login" className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded">
            Entrar
          </Link>

          <Link to="/cadastro" className="flex-1 text-center px-4 py-2 bg-gray-200 rounded">
            Cadastrar
          </Link>

          <a
            href={`/checkout?courseId=${course?.id}`}
            className="flex-1 text-center px-4 py-2 bg-green-600 text-white rounded"
          >
            Comprar
          </a>
        </div>

        <button onClick={onClose} className="mt-4 text-sm text-gray-500">
          Fechar (continuar em modo demonstração)
        </button>
      </div>
    </div>
  );
};

/* ============================================================
   PLAYER
============================================================ */
const LessonPlayer = ({ lesson, blocked }) => {
  if (!lesson) return null;

  const { content_type, content, title } = lesson;
  const type = (content_type || "").toUpperCase();
  const fileUrl = getContentUrl(content);

  if (type === "YOUTUBE" || type === "VIDEO") {
    let embed = content;

    try {
      const match = content.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/);
      if (match) embed = `https://www.youtube.com/embed/${match[1]}?rel=0`;
    } catch {}

    return (
      <div className="relative">
        <div className="aspect-w-16 aspect-h-9 bg-black">
          <iframe
            title={title}
            src={embed}
            allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>

        {blocked && (
          <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center text-white z-10">
            <div className="text-center">
              <h3 className="font-bold text-xl mb-2">⏸ Pré-visualização encerrada</h3>
              <p>Faça login ou compre o curso para continuar.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (type === "PDF") {
    return (
      <div className="relative">
        <iframe src={fileUrl} title={title} className="w-full h-[720px] border" />

        {blocked && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
            <div className="text-center">
              <h3 className="font-bold text-lg">⏸ Pré-visualização finalizada</h3>
              <p>Cadastre-se ou compre para continuar.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (type === "WORD" || type === "DOWNLOAD") {
    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

    return (
      <div className="relative">
        <iframe src={officeUrl} title={title} className="w-full h-[720px] border" />

        {blocked && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
            <div className="text-center">
              <h3 className="font-bold text-lg">⏸ Pré-visualização encerrada</h3>
              <p>Cadastre-se ou compre para continuar.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <p>Conteúdo não suportado.</p>;
};

/* ============================================================
   PÁGINA PRINCIPAL
============================================================ */
export default function PublicCoursePreview() {
  const { id } = useParams();

  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const [blocked, setBlocked] = useState(false);

  const timerRef = useRef(null);
  const previewStartedRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/courses/public/${id}`);

        setCourse(res.data.course || null);
        setLessons(res.data.previewLessons || []);
        setSelectedIdx(0);
      } catch (err) {
        console.error("Erro ao carregar curso público:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  useEffect(() => {
    clearTimer();
    previewStartedRef.current = false;
    setBlocked(false);

    if (!lessons[selectedIdx]) {
      setSecondsRemaining(null);
      return;
    }

    const sec = Number(lessons[selectedIdx].preview_seconds || 20);
    setSecondsRemaining(sec);
  }, [selectedIdx, lessons]);

  useEffect(() => () => clearTimer(), []);

  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const startPreview = () => {
    if (!lessons[selectedIdx] || previewStartedRef.current) return;

    previewStartedRef.current = true;
    setBlocked(false);

    let sec = Number(lessons[selectedIdx].preview_seconds || 20);
    setSecondsRemaining(sec);

    timerRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearTimer();
          setBlocked(true);
          previewStartedRef.current = false;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-gray-600">Carregando curso...</div>
      </div>
    );

  if (!course)
    return (
      <div className="p-10">
        <h2 className="text-xl font-bold">Curso não encontrado</h2>
        <p>Verifique o link ou tente novamente.</p>
      </div>
    );

  const selectedLesson = lessons[selectedIdx];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* HEADER */}
      <div className="mb-6 flex items-center gap-6">
        {course.cover_image_url ? (
          <img
            src={getContentUrl(course.cover_image_url)}
            alt={course.title}
            className="w-36 h-24 object-cover rounded"
          />
        ) : (
          <div className="w-36 h-24 bg-gray-100 rounded flex items-center justify-center">
            Imagem
          </div>
        )}

        <div>
          <h1 className="text-2xl font-bold">{course.title}</h1>
          <p className="text-sm text-gray-600 mt-1">{course.description}</p>
        </div>
      </div>

      {/* GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* PLAYER */}
        <div className="lg:col-span-3">
          {selectedLesson ? (
            <>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-lg">{selectedLesson.title}</h2>
                  <p className="text-sm text-gray-600">
                    Pré-visualização: {selectedLesson.preview_seconds || 20}s
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-700">
                    {secondsRemaining !== null ? (
                      <span>
                        Tempo restante: <strong>{secondsRemaining}s</strong>
                      </span>
                    ) : (
                      <span>Pronto para iniciar</span>
                    )}
                  </div>

                  {!previewStartedRef.current && !blocked && (
                    <button
                      onClick={startPreview}
                      className="px-3 py-2 bg-blue-600 text-white rounded"
                    >
                      Iniciar pré-visualização
                    </button>
                  )}
                </div>
              </div>

              <LessonPlayer lesson={selectedLesson} blocked={blocked} />

              {blocked && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-800 font-medium">
                    Tempo de degustação encerrado.
                  </p>
                  <p className="text-sm text-gray-700">
                    Cadastre-se ou compre o curso para continuar.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="p-6 bg-gray-50 rounded">
              Nenhuma aula disponível.
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-3">
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-bold mb-2">Aulas gratuitas</h3>

            <ul className="space-y-2">
              {lessons.length === 0 && (
                <li className="text-sm text-gray-500">Sem aulas liberadas</li>
              )}

              {lessons.map((l, i) => (
                <li key={l.id}>
                  <button
                    className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 ${
                      selectedIdx === i ? "bg-indigo-50 font-semibold" : ""
                    }`}
                    onClick={() => setSelectedIdx(i)}
                  >
                    <div className="flex justify-between items-center">
                      <span>{l.title}</span>
                      <span className="text-xs text-gray-500">
                        {l.preview_seconds || 20}s
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white p-4 rounded shadow">
            <h4 className="font-bold mb-2">Acesso completo</h4>
            <p className="text-sm text-gray-700 mb-2">
              Disponível após aquisição do curso.
            </p>

            <div className="flex gap-2">
              <Link
                to="/login"
                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-center"
              >
                Entrar
              </Link>
              <Link
                to="/cadastro"
                className="flex-1 px-3 py-2 border rounded text-center"
              >
                Cadastrar
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <CTAmodal open={blocked} onClose={() => setBlocked(false)} course={course} />
    </div>
  );
}
